import React, { useState, useEffect, useRef } from 'react';
import { DisplayableSearchResult } from '../types';
import { addUtmParametersToUrl } from '../utils';

interface ProductCardProps {
    product: DisplayableSearchResult;
    onCardHover: (productId: string | null) => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product, onCardHover }) => {
    const [imageLoadFailed, setImageLoadFailed] = useState(false);
    const [displayedImageIndex, setDisplayedImageIndex] = useState(0);
    const [isCardHovered, setIsCardHovered] = useState(false);

    const imagesToDisplay = product.images.filter(Boolean);
    const hasMultipleImages = imagesToDisplay.length > 1;

    useEffect(() => {
        if (hasMultipleImages && isCardHovered && !imageLoadFailed) {
            const interval = setInterval(() => {
                setDisplayedImageIndex(prev => (prev + 1) % imagesToDisplay.length);
            }, 1800);
            return () => clearInterval(interval);
        }
    }, [isCardHovered, hasMultipleImages, imagesToDisplay.length, imageLoadFailed]);

    const productTitle = product.title || 'Unbenanntes Produkt';
    const priceToDisplay = product.price || 'Preis auf Anfrage';
    const fallbackTextContent = product.brand || productTitle.trim().split(' ')[0] || 'Artikel';
    const productUrlWithUtm = addUtmParametersToUrl(product.link);

    const availabilityInfo = product.availability === 'in_stock'
        ? { text: 'Auf Lager', labelClass: 'bg-green-600' }
        : product.availability === 'out_of_stock'
        ? { text: 'Ausverkauft', labelClass: 'bg-red-600' }
        : product.availability === 'preorder'
        ? { text: 'Vorbestellung', labelClass: 'bg-yellow-600' }
        : null;

    const currentImageSrc = imagesToDisplay[displayedImageIndex] || null;
    const showFallback = !currentImageSrc || imageLoadFailed;

    return (
        <a
            href={productUrlWithUtm}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative bg-white rounded-xl shadow-lg overflow-hidden flex flex-col border border-gray-200 hover:shadow-xl transition-shadow duration-300 ease-in-out no-underline h-full"
            style={{ textDecoration: 'none' }}
            onMouseEnter={() => { onCardHover(product.id); setIsCardHovered(true); }}
            onMouseLeave={() => { onCardHover(null); setIsCardHovered(false); }}
        >
            <div className="relative w-full h-32 overflow-hidden bg-gray-200">
                {!showFallback && currentImageSrc && (
                    <img
                        src={currentImageSrc}
                        alt={productTitle}
                        className="absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-300 ease-in-out"
                        onError={() => setImageLoadFailed(true)}
                    />
                )}
                {showFallback && (
                    <div className="w-full h-full flex items-center justify-center">
                        <span className="text-gray-700 text-sm font-medium p-2 text-center">{fallbackTextContent}</span>
                    </div>
                )}
                
                {availabilityInfo && (
                    <div className={`absolute top-1.5 left-1.5 text-white text-xxs font-bold px-1.5 py-0.5 rounded-md shadow z-20 ${availabilityInfo.labelClass}`}>
                        {availabilityInfo.text}
                    </div>
                )}
            </div>
            <div className="p-3 flex flex-col flex-grow">
                <h3 className="text-sm font-semibold text-gray-800 mb-1 truncate" title={productTitle}>
                    {productTitle}
                </h3>
                <p className="text-base font-bold text-[#0066CC]">
                    {priceToDisplay}
                </p>
                <div className="mt-auto pt-1.5 border-t border-gray-100">
                    <div className="w-full flex items-center justify-center bg-[#0066CC] group-hover:bg-[#004499] text-white text-xs font-medium py-1.5 px-3 rounded-md transition-colors duration-150 ease-in-out">
                        Zum Produkt
                    </div>
                </div>
            </div>
        </a>
    );
};
